import axios, {AxiosResponse} from 'axios';
import {downloadFile} from '../../../../../utils/downloadFile';
import type {PriceQuoteDetails} from "../../types";

export const GET_PRICE_QUOTE_DETAILS = '/priceQuotes/getPriceQuoteDetails';
export const ADD_EDIT_PRICE_QUOTE = '/priceQuotes/addEditPriceQuote';
export const UPLOAD_FILES_PRICE_QUOTE = '/priceQuotes/uploadFiles';
export const DELETE_FILE_PRICE_QUOTE = '/priceQuotes/deleteFile';
export const DOWNLOAD_FILE_PRICE_QUOTE = '/priceQuotes/getFile';

interface ApiResponse<T> {
  errorCode: number;
  body: T;
}

interface AddEditPriceQuoteRequestBody {
  priceQuoteID: number | string;
  status: { value: number | string };
  customers: { customerID: number }[];
  contactID: number | null;
  lines: {
    description: string,
    quantity: string,
    price: number,
    vatType: number
  }[];
  totalWithVat: number
}

export const getPriceQuoteDetails = async (priceQuoteID: number | string): Promise<PriceQuoteDetails | undefined> => {
  if (priceQuoteID && priceQuoteID !== -1 && priceQuoteID !== '-1') {
    const data = {priceQuoteID: parseInt(priceQuoteID.toString(), 10)};

    try {
      const response: AxiosResponse<ApiResponse<PriceQuoteDetails>> = await axios.post(GET_PRICE_QUOTE_DETAILS, data);
      const {errorCode, body} = response.data;

      if (errorCode < 0) throw new Error("Error fetching price quote details");

      return body;
    } catch (error: any) {
      error.clientMessage = "Can't find data";
      console.error(error);
      return undefined;
    }
  }
};

export const addEditPriceQuote = async ({
                                          priceQuoteID,
                                          status,
                                          customers,
                                          contactID,
                                          lines,
                                          totalWithVat,
                                        }: AddEditPriceQuoteRequestBody): Promise<number | undefined> => {
  const data = {
    priceQuoteID: parseInt(priceQuoteID.toString(), 10),
    status: parseInt(status.value.toString(), 10),
    customers: customers.map((item) => item.customerID),
    contactID: contactID ? parseInt(contactID.toString(), 10) : null,
    lines: lines || [],
    totalWithVat
  };

  try {
    const response: AxiosResponse<ApiResponse<{ priceQuoteID: number }>> = await axios.post(ADD_EDIT_PRICE_QUOTE, data);
    const { errorCode, body } = response.data;

    if (errorCode < 0) throw new Error("Error creating/editing price quote");

    return body?.priceQuoteID ? body.priceQuoteID : 0;
  } catch (error: any) {
    error.clientMessage = "Can't create/edit document";
    console.error(error);
    return 0;
  }
};

export const uploadPriceQuoteFile = async (formData: FormData) => {
  try {
    await axios.post(UPLOAD_FILES_PRICE_QUOTE, formData,
      {
      headers: {'Content-Type': 'multipart/form-data'},
    });
  } catch (err) {
    throw new Error();
    console.error(`uploadFile returns error: ${err}`);
  }
};

export const deletePriceQuoteFile = async (fileID: number): Promise<void> => {
  try {
    const response: AxiosResponse<ApiResponse<null>> = await axios.post(DELETE_FILE_PRICE_QUOTE, { fileID });
    const { errorCode } = response.data;

    if (errorCode < 0) throw new Error("Error deleting file");
  } catch (error: any) {
    error.clientMessage = "Can't delete file";
    console.error(error);
  }
};

export const downloadPriceQuoteFile = async (priceQuoteID: number, fileID: number, fileName: string): Promise<string | undefined> => {
  try {
    const response: AxiosResponse<ApiResponse<{ file: string }>> = await axios.post(
      DOWNLOAD_FILE_PRICE_QUOTE, { priceQuoteID, fileID }
    );
    const { errorCode, body } = response.data;

    if (errorCode < 0) throw new Error("Error downloading file");

    downloadFile(`data:;base64,${body.file}`, fileName);

    return body.file;
  } catch (error: any) {
    error.clientMessage = "Can't find file";
    console.error(error);
    return undefined;
  }
};

export const previewPriceQuoteFile = async (priceQuoteID: number, fileID: number): Promise<string> => {
  try {
    const response: AxiosResponse<ApiResponse<{ file: string }>> = await axios.post(
      DOWNLOAD_FILE_PRICE_QUOTE, { priceQuoteID, fileID
      });
    const {errorCode, body} = response.data;

    if (errorCode < 0) throw new Error("Error previewing file");

    return body?.file ?? '';
  } catch (error: any) {
    error.clientMessage = "Can't find file";
    console.error(error);
    return '';
  }
};